import styles from './style.module.scss';
import { IoCloseSharp } from "react-icons/io5";
import { FaAngleLeft } from "react-icons/fa";
import api from 'helpers/api';
import { useEffect, useRef, useState } from 'react';
import Loader from 'components/miscellaneous/Loader/Loader';
import { setHasNewChanges } from 'store/auth-slice';
import { useDispatch } from 'react-redux';
import ChangeLogList from './ChangeLogList/ChangeLogList';
import EditChangeLog from './EditChangeLog/EditChangeLog';
import CreateChangeLog from './CreateChangeLog/CreateChangeLog';
import SingleLog from './SingleLog/SingleLog';
import HasPermission from 'helpers/permissions/HasPermission';

const WhatsNewDialog = ({ onClose, isVisible, userRole }) => {
    const dispatch = useDispatch();
    const [ changelog, setChangelog ] = useState([]);
    const [ visibleItems, setVisibleItems ] = useState([]);
    const [ isLoading, setIsLoading ] = useState(true);
    const [ isViewing, setIsViewing ] = useState(null);
    const [ isEditing, setIsEditing ] = useState(null);
    const [ isCreatingNew, setIsCreatingNew ] = useState(false);

    const containerRef = useRef(null)
    const cardsRef = useRef([]);

    const reportOnClose = () => {
        setIsCreatingNew(false);
        setIsEditing(null);
        setVisibleItems([]);
        setIsLoading(false);

        if (onClose) {
            onClose()
        }
    }

    const getChangeLog = async () => {
        setIsLoading(true)

        try {
            const res = await api.get('/changelog')
            if (res.success && res.data.data) {
                setChangelog(res.data.data)
            }
        } catch (e) {

        } finally {
            setIsLoading(false)
        }
    }

    const updateAsViewed = async (ids) => {
        try {
            const res = await api.patch('/changelog/viewed', {
                ids
            });

            if (res.success && res.data.data) {
                const hasSeenNewChanges = res.data.data.seen_new_changes;
                dispatch(setHasNewChanges(!hasSeenNewChanges));
            }
        } catch (e) {

        } finally {

        }
    }

    const onScroll = () => {
        setVisibleItems(getVisibleItems())
    }

    const getVisibleItems = () => {
        const scrollTop = containerRef.current.scrollTop
        const containerHeight = containerRef.current.clientHeight
        const containerPosition = [ scrollTop, scrollTop + containerHeight ]

        return getItemPositions().filter(item => item.startsAt >= containerPosition[ 0 ] && item.midAt <= containerPosition[ 1 ]);
    }

    const getItemPositions = () => {
        const itemPositions = [];

        if (cardsRef.current.length > 0) {
            const containerHeight = containerRef.current.clientHeight

            cardsRef.current.map((card, index) => {
                if (card !== null) {
                    const itemHeight = (card.offsetTop + card.clientHeight) - card.offsetTop;
                    const minContainerHeight = itemHeight - (itemHeight / 2);
                    const midAt = card.offsetTop + (itemHeight / 2);

                    itemPositions.push({
                        startsAt: card.offsetTop,
                        midAt: minContainerHeight > containerHeight ? containerHeight : midAt,
                        endsAt: card.offsetTop + card.clientHeight,
                        id: parseInt(card.getAttribute('logId'))
                    })
                }
            })
        }

        return itemPositions;
    }

    const getChangeLogById = (id) => {
        return changelog.find(item => item.id === id)
    }

    useEffect(() => {
        getChangeLog()
    }, [])

    useEffect(() => {
        if (isVisible && isEditing == null && isCreatingNew === false) {
            if (changelog.length > 0) {
                setVisibleItems(getVisibleItems())
            }
        }
    }, [ changelog, isVisible ])

    useEffect(() => {
        if (visibleItems.length > 0 && Array.isArray(changelog)) {
            const toUpdateIds = visibleItems.filter((item) => {
                const log = getChangeLogById(item.id)
                return log && log.viewed === false
            }).map(item => item.id)

            if (toUpdateIds.length > 0) {
                setChangelog([
                    ...changelog.map((c) => {
                        if (toUpdateIds.find(id => id === c.id)) {
                            c.viewed = true
                        }

                        return c;
                    }),
                ])

                updateAsViewed(toUpdateIds)
            }
        }
    }, [ visibleItems ])

    const renderDialogScreen = () => {
        if (isEditing) {
            return (<EditChangeLog log={ isEditing } />)
        } else if (isCreatingNew) {
            return (<CreateChangeLog refresh={ () => {
                setIsCreatingNew(false);
                getChangeLog()
            } } />)
        } else if (isViewing) {
            return (<>
                <SingleLog log={ isViewing } />
            </>)
        } else {
            return (<ChangeLogList refresh={ getChangeLog } changelog={ changelog } userRole={ userRole } cardsRef={ cardsRef } setIsViewing={ setIsViewing } setIsCreatingNew={ setIsCreatingNew } setIsEditing={ setIsEditing } />)
        }
    }

    const renderDialogHeader = () => {
        if (isEditing) {
            return (<>
                <button onClick={ (e) => {
                    setIsEditing(null)
                    getChangeLog()
                } } className={ styles.goBack }><FaAngleLeft /> Go Back</button>
                <h1 style={ {
                    margin: '0 auto'
                } }>Editing Log Record</h1>
            </>)
        } else if (isCreatingNew) {
            return (<>
                <button onClick={ (e) => {
                    setIsEditing(null)
                    setIsCreatingNew(false)
                    getChangeLog()
                } } className={ styles.goBack }><FaAngleLeft /> Go Back</button>
                <h1 style={ {
                    margin: '0 auto'
                } }>Add record to Changelog</h1>
            </>)
        } else if (isViewing) {
            return (<>
                <button onClick={ (e) => {
                    setIsEditing(null)
                    setIsViewing(null)
                } } className={ styles.goBack }><FaAngleLeft /> View All</button>
                <h1 style={ {
                    margin: '0 auto'
                } }></h1>
            </>)
        }

        return (<div className='d-flex align-items-center'>
            <h1>What's New On SmartClinic</h1>
            { userRole === 5 && <HasPermission permission="changelog.create">
                <button style={ {
                    margin: '0 0 0 10px',
                    padding: '5px 10px'
                } } onClick={ () => {
                    setIsCreatingNew(true)
                } } className='white-button button-small'><b>+</b></button>
            </HasPermission> }
        </div>)


    }

    return (
        <div className={ `${styles[ 'container' ]}` } ref={ containerRef } onScroll={ onScroll }>
            <div className={ styles[ 'header' ] }>
                { renderDialogHeader() }
                <button className={ styles.close } onClick={ (e) => {
                    reportOnClose(false)
                } }>
                    <IoCloseSharp />
                </button>
            </div>

            <section className={ styles[ 'grid' ] }>
                <div className={ styles[ 'content' ] }>
                    { !isLoading ? <>
                        { renderDialogScreen() }
                    </> : <Loader color="#ddd" /> }
                </div>
            </section>

        </div>
    )
}

export default WhatsNewDialog;